<template>
  <div class="msg-viewer-container">
    <!-- Header Section -->
    <div class="msg-header-section">
      <div class="msg-header-row">
        <span class="msg-field-label">From:</span>
        <span class="msg-field-value">{{ msgData.From }}</span>
      </div>
      <div class="msg-header-row">
        <span class="msg-field-label">To:</span>
        <span class="msg-field-value">{{ msgData.To }}</span>
      </div>
      <div v-if="msgData.CC" class="msg-header-row">
        <span class="msg-field-label">CC:</span>
        <span class="msg-field-value">{{ msgData.CC }}</span>
      </div>
      <div class="msg-header-row">
        <span class="msg-field-label">Subject:</span>
        <span class="msg-field-value msg-subject">{{ msgData.Subject }}</span>
      </div>
      <div class="msg-header-row">
        <span class="msg-field-label">Date:</span>
        <span class="msg-field-value">{{ msgData.Date }}</span>
      </div>
    </div>
    <!-- Attachments Section -->
    <div
      v-if="msgData.Attachments && msgData.Attachments.length > 0"
      class="msg-attachments-section"
    >
      <div class="attachment-wrapper">
        <div class="attachment-label">Attachments ({{ msgData.Attachments.length }})</div>
        <div class="attachment-container">
          <VasionTooltip
            v-for="(attachment, index) in msgData.Attachments"
            :key="index"
            :text="attachment.FileName"
            position="top"
          >
            <div class="attachment-item" @click="handleAttachmentClick(attachment)">
              <div class="attachment-details">
                <span class="attachment-name">{{ attachment.FileName }}</span>
                <span class="attachment-size">({{ formatFileSize(attachment.Size) }})</span>
              </div>
            </div>
          </VasionTooltip>
        </div>
      </div>
    </div>
    <!-- Email Body Section -->
    <div class="msg-body-section">
      <HTMLViewer :source="msgData.HtmlData" />
    </div>
  </div>
</template>
<script>
import HTMLViewer from '@/components/document/HTMLViewer.vue'

export default {
  name: 'MsgViewer',
  components: {
    HTMLViewer,
  },
  props: {
    documentId: {
      type: Number,
      required: true,
    },
    msgData: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value &&
          typeof value.To === 'string' &&
          typeof value.From === 'string' &&
          typeof value.CC === 'string' &&
          typeof value.Subject === 'string' &&
          typeof value.Date === 'string' &&
          Array.isArray(value.Attachments) &&
          typeof value.HtmlData === 'string'
        )
      },
    },
  },
  data() {
    return {}
  },
  methods: {
    handleAttachmentClick(attachment) {
      if (!attachment || !attachment.FileName) return
      if (!this.documentId) return

      const routeData = this.$router.resolve({
        name: 'MinimalDocumentViewer',
        params: {
          documentId: this.documentId,
          attachmentName: attachment.FileName,
        },
      })

      window.open(routeData.href, '_blank')
    },
    formatFileSize(sizeStr) {
      const sizeInBytes = parseInt(sizeStr, 10)
      if (isNaN(sizeInBytes)) return sizeStr

      const sizeInKB = Math.ceil(sizeInBytes / 1024)
      return sizeInKB.toLocaleString() + ' KB'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.msg-viewer-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: $grey-scale-white;
  font-family: $font-family-book;
  overflow: hidden;
}
.msg-header-section {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid $outline-variant;
  background-color: $surface-container;
  max-height: 130px;
  overflow-y: auto;
}
.msg-header-row {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
}
.msg-field-label {
  font-family: $font-family-book;
  font-size: 14px;
  font-weight: normal;
  line-height: normal;
  color: $on-surface-variant;
  min-width: 80px;
  flex-shrink: 0;
  font-weight: 500;
}
.msg-field-value {
  font-family: $font-family-book;
  font-size: 14px;
  font-weight: normal;
  line-height: normal;
  color: $on-surface;
  flex: 1;
  word-break: break-word;
  line-height: 1.4;
}
.msg-subject {
  font-weight: 600;
  color: $on-surface;
}
.msg-attachments-section {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid $outline-variant;
  background-color: $bg-gray-25;
  max-height: 100px;
  overflow-y: auto;
}
.attachment-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.attachment-label {
  font-size: 13px;
  font-weight: 600;
  color: $on-surface;
  text-align: left;
}
.attachment-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  background-color: $bg-gray-25;
  border-radius: 4px;
}
.attachment-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: $surface-container;
  border: 1px solid $outline-variant;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.55, 0, 0.55, 0.2);
  min-width: 120px;
  max-width: 200px;

  &:hover {
    background-color: $primary-container;
    border-color: $primary;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background-color: $surface-variant;
    transform: translateY(1px);
  }
}
.attachment-details {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.attachment-name {
  font-size: 12px;
  font-weight: 500;
  color: $on-surface;
  margin-right: 6px;
}
.attachment-size {
  font-size: 11px;
  color: $on-surface-variant;
}
.msg-body-section {
  flex: 1;
  padding: 8px 0;
  overflow-y: auto;
  background-color: $surface-container;
}
</style>
